import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  HStack,
  VStack,
  Spinner,
  Divider,
  Image,
  Text,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useOutsideClick,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import api from '../api';
import CategoryFilter from '../components/CategoryFilter';
import CreateProduct from '../components/CreateProduct';
import ProductDetailModal from '../components/ProductDetailModal';
import PrintProductsList from '../components/PrintProductsList';
import usePagination from '../utils/usePagination';


const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [isFullImageOpen, setIsFullImageOpen] = useState(false);
  const [fullImageSrc, setFullImageSrc] = useState('');
  const [isCreateProductOpen, setCreateProductOpen] = useState(false);
  const [isEditProductOpen, setEditProductOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    selectedCategories: [],
    minPrice: '',
    maxPrice: '',
    isVat: false,
  });

  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  const placeholderImage = '/placeholder-product.jpeg';
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const fetchProducts = async (url = '/products/') => {
    setLoading(true);

    const params = new URLSearchParams();
    if (filters.name) params.append('search', filters.name);
    if (filters.minPrice) params.append('min_price', filters.minPrice);
    if (filters.maxPrice) params.append('max_price', filters.maxPrice);
    if (filters.isVat) params.append('is_vat', filters.isVat);
    if (filters.selectedCategories.length) {
      params.append(
        'categories',
        filters.selectedCategories.map((cat) => cat.id).join(',')
      );
    }

    const fullUrl = `${url}${params.toString() ? `?${params.toString()}` : ''}`.replace('??', '?');

    try {
      const response = await api.get(fullUrl);
      setProducts(response.data.results);
      updatePagination(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const { pagination, updatePagination, fetchNext, fetchPrevious, goToPage } = usePagination(fetchProducts, '/products/');

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  // Pre-warm the gradient animation to prevent first-load delay
  useEffect(() => {
    // Inject global keyframes if not already present
    if (!document.querySelector('#gradient-keyframes')) {
      const style = document.createElement('style');
      style.id = 'gradient-keyframes';
      style.textContent = `
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `;
      document.head.appendChild(style);
    }

    // Create a temporary element to initialize the gradient animation
    const tempElement = document.createElement('div');
    tempElement.style.cssText = `
      position: absolute;
      top: -1px;
      left: -1px;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
      background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
      background-size: 400% 400%;
      animation: gradientShift 0.1s ease;
    `;
    document.body.appendChild(tempElement);
    
    // Remove after a short delay
    setTimeout(() => {
      if (document.body.contains(tempElement)) {
        document.body.removeChild(tempElement);
      }
    }, 100);
  }, []);

  const handleOpenCreateProduct = () => setCreateProductOpen(true);
  const handleCloseCreateProduct = () => setCreateProductOpen(false);

  const handleOpenEditProduct = async (productId, event) => {
    event.stopPropagation();
    try {
      const response = await api.get(`/products/${productId}/`);
      const product = response.data;
  
      // Fetch the full category hierarchy
      const resolveCategories = async (categoryId) => {
        if (!categoryId) return [];
        const categoryResponse = await api.get(`/categories/${categoryId}/`);
        const category = categoryResponse.data;
        const parentPath = await resolveCategories(category.parent);
        return [...parentPath, category];
      };
  
      const fullCategoryPath = await resolveCategories(product.categories[product.categories.length - 1]);
  
      // Set the product for editing
      setProductToEdit({
        id: product.id,
        name: product.name,
        description: product.description,
        price: product.price,
        purchase_price: product.purchase_price,
        markup_percentage: product.markup_percentage,
        stock_quantity: product.stock_quantity,
        catalog_number: product.catalog_number,
        unit_measurement: product.unit_measurement,
        is_vat: product.is_vat,
        categories: fullCategoryPath.map((cat) => cat.id),
        country: product.country,
        images: product.images.map((image, index) => ({
          file: null, // Backend images won't have a file
          preview: image.image, // Image URL from the backend
          id: image.id,
          priority: image.priority || index + 1,
        })),
      });
  
      setEditProductOpen(true);
    } catch (error) {
      console.error('Failed to fetch product data for editing:', error);
    }
  };

  const handleCategoryFilterChange = (updatedCategories) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedCategories: updatedCategories.filter((cat) => !cat.isIndeterminate) // Exclude indeterminate states
    }));
  };

  const handleCloseEditProduct = () => {
    setProductToEdit(null);
    setEditProductOpen(false);
  };

  const handleOpenDeleteConfirm = (product, event) => {
    event.stopPropagation();
    setProductToDelete(product);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => setDeleteConfirmOpen(false);

  const handleProductCreated = () => {
    fetchProducts();
    handleCloseCreateProduct();
  };

  const handleProductUpdated = () => {
    fetchProducts();
    handleCloseEditProduct();
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await api.delete(`/products/${productToDelete.id}/`);
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      handleCloseDeleteConfirm();
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setIsProductModalOpen(false);
  };

  // Keyboard navigation for images
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isProductModalOpen || !selectedProduct || !selectedProduct.images || selectedProduct.images.length <= 1) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setCurrentImageIndex(currentImageIndex < selectedProduct.images.length - 1 ? currentImageIndex + 1 : 0);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : selectedProduct.images.length - 1);
      }
    };

    if (isProductModalOpen) {
      window.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isProductModalOpen, currentImageIndex, selectedProduct]);

  const toggleCategoryFilter = () => {
    if (showCategoryFilter) {
      // If currently showing, close it
      setShowCategoryFilter(false);
    } else {
      // If currently hidden, show it
      setShowCategoryFilter(true);
    }
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      selectedCategories: [],
      minPrice: '',
      maxPrice: '',
      isVat: false,
    });
  };

  const openImagePopup = () => setIsImagePopupOpen(true);
  const closeImagePopup = () => setIsImagePopupOpen(false);

  const openFullImage = (imageSrc) => {
    setFullImageSrc(imageSrc);
    setIsFullImageOpen(true);
  };

  const closeFullImage = () => {
    setIsFullImageOpen(false);
    setFullImageSrc('');
  };

  // Remove the outside click handler since CategoryFilter handles it internally
  // useOutsideClick({
  //   ref: dropdownRef,
  //   handler: () => setShowCategoryFilter(false),
  // });

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Modern Header Card */}
        <Box
          bg={headerBg}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 8 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="visible"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="6px"
            bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)"
          />
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }} w="100%">
            <Box textAlign={{ base: 'center', md: 'left' }} w="full">
              <Heading 
                as="h1" 
                size={{ base: 'lg', md: '2xl' }}
                bgGradient="linear(to-r, #667eea, #764ba2)"
                bgClip="text"
                mb={2}
                fontWeight="bold"
              >
                Товари
              </Heading>
              <Text color={subtleTextColor} fontSize={{ base: 'sm', md: 'lg' }}>
                Управління товарами та продуктами
              </Text>
            </Box>
            <HStack spacing={{ base: 3, md: 4 }} justify={{ base: 'center', md: 'flex-end' }} w="full" flexWrap="wrap">
              <Box flexShrink={0}>
                <PrintProductsList filters={filters} />
              </Box>
              <Button 
                bg="linear-gradient(135deg, #667eea, #764ba2)"
                color="white"
                size={{ base: 'md', md: 'lg' }}
                leftIcon={<AddIcon />}
                onClick={handleOpenCreateProduct}
                borderRadius="xl"
                px={{ base: 6, md: 8 }}
                whiteSpace="nowrap"
                display="inline-flex"
                boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
                _hover={{
                  transform: { base: 'none', md: 'translateY(-2px)' },
                  boxShadow: { base: '0 4px 15px rgba(102, 126, 234, 0.4)', md: '0 8px 25px rgba(102, 126, 234, 0.6)' },
                  transition: 'all 0.2s ease'
                }}
                _active={{ transform: 'translateY(0)' }}
              >
                Створити новий товар
              </Button>
            </HStack>
          </Flex>
        </Box>
        {/* Modern Filters Card */}
        <Box
          bg={cardBg}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 10px 15px -3px ${shadowColor}`}
          position="relative"
          overflow="visible"
          zIndex={10}
        >
          {/* Subtle gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #f093fb, #f5576c)"
            opacity={0.8}
          />
          <VStack spacing={{ base: 4, md: 6 }} align="flex-start" w="full">
            {/* Search Field */}
            <HStack spacing={4} w="full">
              <Box position="relative" flex="1"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bg: 'linear-gradient(90deg, #f093fb, #f5576c)',
                  borderRadius: 'lg',
                  padding: '1px',
                  zIndex: -1,
                }}
              >
                <Input
                  placeholder="🔍 Пошук товарів..."
                  name="name"
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  bg={cardBg}
                  border="none"
                  borderRadius="lg"
                  fontSize={{ base: 'sm', md: 'md' }}
                  _focus={{ boxShadow: '0 0 0 3px rgba(240, 147, 251, 0.3)' }}
                />
              </Box>
            </HStack>
            {/* Category and Price Filters */}
            <HStack spacing={4} w="full" flexWrap="wrap" position="relative">
              <Box position="relative" zIndex={100}>
                <Button 
                  onClick={toggleCategoryFilter}
                  bg="linear-gradient(135deg, #23a6d5, #23d5ab)"
                  color="white"
                  borderRadius="xl"
                  px={{ base: 4, md: 6 }}
                  boxShadow="0 4px 6px rgba(35, 166, 213, 0.3)"
                  _hover={{
                    bg: 'linear-gradient(135deg, #1e95c4, #1ec49a)',
                    transform: { base: 'none', md: 'translateY(-1px)' },
                    boxShadow: '0 6px 15px rgba(35, 166, 213, 0.4)',
                  }}
                >
                  {showCategoryFilter ? '🔼 Заховати категорії' : '🔽 Показати категорії'}
                </Button>
                {showCategoryFilter && (
                  <Box position="absolute" top="100%" left="0" zIndex={9999} mt="2"
                    sx={{ zIndex: 9999, position: 'absolute !important' }}
                  >
                    <CategoryFilter
                      selectedCategories={filters.selectedCategories}
                      onCategoryChange={handleCategoryFilterChange}
                      onClose={() => setShowCategoryFilter(false)}
                    />
                  </Box>
                )}
              </Box>
              <Input
                placeholder="💰 Мін. ціна"
                name="minPrice"
                type="number"
                value={filters.minPrice}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                width={{ base: '140px', md: '180px' }}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="lg"
                _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }}
              />
              <Input
                placeholder="💰 Макс. ціна"
                name="maxPrice"
                type="number"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                width={{ base: '140px', md: '180px' }}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="lg"
                _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }}
              />
              <Button 
                onClick={clearFilters}
                bg="linear-gradient(135deg, #ee7752, #e73c7e)"
                color="white"
                borderRadius="xl"
                px={{ base: 4, md: 6 }}
                boxShadow="0 4px 6px rgba(231, 60, 126, 0.3)"
                _hover={{
                  bg: 'linear-gradient(135deg, #e66641, #d6356f)',
                  transform: { base: 'none', md: 'translateY(-1px)' },
                  boxShadow: '0 6px 15px rgba(231, 60, 126, 0.4)',
                }}
              >
                🗑️ Очистити фільтри
              </Button>
            </HStack>
          </VStack>
        </Box>
        {/* Product List Section */}
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minH="300px" bg={cardBg} borderRadius={{ base: 'lg', md: '2xl' }} border="1px solid" borderColor={borderColor}>
              <VStack spacing={4}>
                <Spinner size="xl" color="#667eea" thickness="4px" />
                <Text color={subtleTextColor} fontSize={{ base: 'md', md: 'lg' }}>Завантаження товарів...</Text>
              </VStack>
            </Box>
          ) : (
            <Box>
              {products.length ? (
                <Box>
                  {/* Modern Product Grid */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                    {products.map((product) => (
                      <Box
                        key={product.id}
                        bg={cardBg}
                        borderRadius={{ base: 'lg', md: '2xl' }}
                        border="1px solid"
                        borderColor={borderColor}
                        p={{ base: 4, md: 6 }}
                        minH="220px"
                        boxShadow={`0 10px 15px -3px ${shadowColor}`}
                        transition="all 0.3s ease"
                        _hover={{ transform: { base: 'none', md: 'translateY(-5px)' }, boxShadow: { base: `0 10px 15px -3px ${shadowColor}`, md: `0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}` } }}
                        onClick={() => handleProductClick(product)}
                        cursor="pointer"
                        position="relative"
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                      >
                        {/* Gradient accent */}
                        <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #4facfe, #00f2fe)" />
                        <Box mb={4}>
                          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textColor} noOfLines={2} lineHeight="1.2">
                            {product.name}
                          </Text>
                        </Box>
                        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-start' }} justify="space-between" flex="1" gap={{ base: 3, md: 0 }}>
                          {/* Product Image */}
                          <Box boxSize={{ base: '100px', md: '120px' }} borderRadius="xl" overflow="hidden" mr={{ base: 0, md: 6 }} border="2px solid" borderColor={borderColor} flexShrink={0}>
                            <Image src={product.images[0]?.image || placeholderImage} alt={product.name} boxSize="100%" objectFit="cover" transition="transform 0.3s ease" _hover={{ transform: { base: 'none', md: 'scale(1.05)' } }} />
                          </Box>
                          {/* Product Details */}
                          <VStack align="flex-start" spacing={2} flex="1">
                            <HStack spacing={2} flexWrap="wrap">
                              <Badge colorScheme="blue" borderRadius="md" px={2}>ID: {product.id}</Badge>
                              <Badge colorScheme="purple" borderRadius="md" px={2}>{product.catalog_number}</Badge>
                            </HStack>
                            <Text fontSize={{ base: 'xs', md: 'sm' }} color={subtleTextColor}>📦 Кількість: {product.stock_quantity} {product.unit_measurement}</Text>
                            <Box>
                              <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor} bgGradient="linear(to-r, #4facfe, #00f2fe)" bgClip="text">
                                💰 {Number(product.price).toFixed(2)} грн
                              </Text>
                              {product.is_vat && (
                                <Text fontSize={{ base: 'sm', md: 'md' }} color={subtleTextColor} mt={1}>З ПДВ: {Number(product.price_with_vat).toFixed(2)} грн</Text>
                              )}
                            </Box>
                          </VStack>
                        </Flex>
                        {/* Action Buttons - Bottom Right */}
                        <Flex justify="flex-end" mt={4}>
                          <HStack spacing={2}>
                            <IconButton icon={<EditIcon />} aria-label="Edit product" onClick={(event) => handleOpenEditProduct(product.id, event)} bg="linear-gradient(135deg, #F59E0B, #D97706)" color="white" borderRadius="lg" size={{ base: 'sm', md: 'md' }} boxShadow="0 4px 6px rgba(245, 158, 11, 0.3)" _hover={{ bg: 'linear-gradient(135deg, #E49307, #C76E05)', transform: { base: 'none', md: 'translateY(-1px)' }, boxShadow: '0 6px 15px rgba(245, 158, 11, 0.4)' }} _active={{ transform: 'translateY(0)' }} />
                            <IconButton icon={<DeleteIcon />} aria-label="Delete product" onClick={(event) => handleOpenDeleteConfirm(product, event)} bg="linear-gradient(135deg, #EF4444, #DC2626)" color="white" borderRadius="lg" size={{ base: 'sm', md: 'md' }} boxShadow="0 4px 6px rgba(239, 68, 68, 0.3)" _hover={{ bg: 'linear-gradient(135deg, #E33E3E, #C51E1E)', transform: { base: 'none', md: 'translateY(-1px)' }, boxShadow: '0 6px 15px rgba(239, 68, 68, 0.4)' }} _active={{ transform: 'translateY(0)' }} />
                          </HStack>
                        </Flex>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              ) : (
                <Box bg={cardBg} borderRadius={{ base: 'lg', md: '2xl' }} border="1px solid" borderColor={borderColor} p={{ base: 8, md: 12 }} textAlign="center" boxShadow={`0 10px 15px -3px ${shadowColor}`}>
                  <Text fontSize={{ base: 'lg', md: 'xl' }} color={subtleTextColor} mb={2}>😔 Товари не знайдено</Text>
                  <Text color={subtleTextColor}>Спробуйте змінити фільтри або додайте новий товар</Text>
                </Box>
              )}
            </Box>
          )}
          {/* Modern Pagination */}
          {pagination.count > 0 && (
            <Box bg={cardBg} borderRadius={{ base: 'lg', md: '2xl' }} border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} mt={{ base: 6, md: 8 }} boxShadow={`0 10px 15px -3px ${shadowColor}`}>
              <Flex justify="center" align="center">
                {pagination.count > 10 && (
                  <HStack spacing={{ base: 2, md: 3 }} flexWrap="wrap" justify="center">
                    <Button 
                      onClick={() => fetchPrevious()} 
                      isDisabled={!pagination.previous} 
                      bg="linear-gradient(135deg, #667eea, #764ba2)" 
                      color="white" 
                      borderRadius="xl" 
                      size={{ base: 'sm', md: 'md' }}
                      px={{ base: 3, md: 6 }} 
                      _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} 
                      _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}
                    >
                      ← Попередня
                    </Button>
                    {Array.from({ length: Math.ceil(pagination.count / 10) }, (_, index) => (
                      <Button
                        key={index + 1}
                        onClick={() => goToPage(index + 1)}
                        size={{ base: 'sm', md: 'md' }}
                        minW={{ base: '36px', md: '40px' }}
                        px={{ base: 2, md: 3 }}
                        bg={pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #f093fb, #f5576c)' : cardBg}
                        color={pagination.currentPage === index + 1 ? 'white' : textColor}
                        border="1px solid"
                        borderColor={pagination.currentPage === index + 1 ? 'transparent' : borderColor}
                        borderRadius="lg"
                        _hover={{
                          bg: pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #e881f9, #f3455a)' : borderColor,
                          transform: { base: 'none', md: 'translateY(-1px)' },
                        }}
                      >
                        {index + 1}
                      </Button>
                    ))}
                    <Button 
                      onClick={() => fetchNext()} 
                      isDisabled={!pagination.next} 
                      bg="linear-gradient(135deg, #667eea, #764ba2)" 
                      color="white" 
                      borderRadius="xl" 
                      size={{ base: 'sm', md: 'md' }}
                      px={{ base: 3, md: 6 }} 
                      _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} 
                      _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}
                    >
                      Наступна →
                    </Button>
                  </HStack>
                )}
              </Flex>
            </Box>
          )}
        </Box>
        {/* Modals */}
        <CreateProduct isOpen={isCreateProductOpen} onClose={handleCloseCreateProduct} onProductCreated={handleProductCreated} />
        {isEditProductOpen && productToEdit && (
          <CreateProduct isOpen={isEditProductOpen} onClose={handleCloseEditProduct} onProductCreated={handleProductUpdated} initialData={productToEdit} />
        )}
        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteConfirmOpen} onClose={handleCloseDeleteConfirm} isCentered>
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent
            bg={cardBg}
            borderRadius="2xl"
            border="1px solid"
            borderColor={borderColor}
            p={6}
            boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
            position="relative"
            overflow="hidden"
          >
            {/* Gradient accent */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="6px"
              bg="linear-gradient(90deg, #EF4444, #DC2626)"
            />
            
            <ModalBody>
              <VStack spacing={6} align="center">
                <Box 
                  boxSize="60px"
                  borderRadius="full"
                  bg="linear-gradient(135deg, #EF4444, #DC2626)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  fontSize="2xl"
                >
                  🗑️
                </Box>
                
                <VStack spacing={3} textAlign="center">
                  <Heading size="lg" color={textColor}>
                    Підтвердити видалення
                  </Heading>
                  <Text color={subtleTextColor} fontSize="md">
                    Ви впевнені, що хочете видалити товар{' '}
                    <Text as="span" fontWeight="bold" color={textColor}>
                      "{productToDelete?.name}"
                    </Text>
                    ?
                  </Text>
                  <Text color={subtleTextColor} fontSize="sm">
                    Цю дію неможливо буде скасувати.
                  </Text>
                </VStack>
                
                <HStack spacing={4} w="full" justify="center">
                  <Button
                    onClick={handleCloseDeleteConfirm}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    color={textColor}
                    borderRadius="xl"
                    px={8}
                    _hover={{
                      bg: borderColor,
                      transform: "translateY(-1px)",
                    }}
                  >
                    Скасувати
                  </Button>
                  <Button
                    onClick={handleDeleteProduct}
                    bg="linear-gradient(135deg, #EF4444, #DC2626)"
                    color="white"
                    borderRadius="xl"
                    px={8}
                    boxShadow="0 4px 6px rgba(239, 68, 68, 0.3)"
                    _hover={{
                      bg: "linear-gradient(135deg, #E33E3E, #C51E1E)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 15px rgba(239, 68, 68, 0.4)",
                    }}
                  >
                    Видалити
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Product Details Modal */}
        <ProductDetailModal
          isOpen={isProductModalOpen}
          onClose={handleCloseProductModal}
          product={selectedProduct}
          onOpenFullImage={openFullImage}
        />

        {/* Image Popup */}
        <Modal isOpen={isImagePopupOpen} onClose={closeImagePopup} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalBody p="0">
              {selectedProduct && (
                <Box>
                  {/* Carousel for images */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                    {selectedProduct.images.map((image, index) => (
                      <Box
                        key={image.id}
                        position="relative"
                        onClick={() => setCurrentImageIndex(index)}
                        cursor="pointer"
                      >
                        <Image
                          src={image.image}
                          alt={selectedProduct.name}
                          borderTopRadius="md"
                          boxSize="100%"
                          objectFit="cover"
                        />
                        {/* Active indicator */}
                        {currentImageIndex === index && (
                          <Box
                            position="absolute"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg="rgba(0, 0, 0, 0.5)"
                            borderTopRadius="md"
                          >
                            <Text fontSize="lg" color="white" fontWeight="bold">
                              Основне зображення
                            </Text>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Full Size Image Modal */}
        <Modal isOpen={isFullImageOpen} onClose={closeFullImage} size="full" isCentered>
          <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(5px)" onClick={closeFullImage} />
          <ModalContent bg="transparent" boxShadow="none" maxW="95vw" maxH="95vh">
            <ModalBody p="0" display="flex" alignItems="center" justifyContent="center" onClick={closeFullImage}>
              <Box position="relative" maxW="100%" maxH="100%" onClick={(e) => e.stopPropagation()}>
                <Image
                  src={fullImageSrc}
                  alt="Full size product image"
                  maxW="100%"
                  maxH="95vh"
                  objectFit="contain"
                  borderRadius="md"
                  boxShadow="2xl"
                  onClick={closeFullImage}
                  cursor="pointer"
                />
                <IconButton
                  icon={<Text fontSize="lg">✕</Text>}
                  aria-label="Close full image"
                  position="absolute"
                  top="10px"
                  right="10px"
                  colorScheme="whiteAlpha"
                  bg="blackAlpha.600"
                  color="white"
                  size="lg"
                  borderRadius="full"
                  onClick={closeFullImage}
                  _hover={{ bg: 'blackAlpha.800' }}
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default Products;
